import { hasErrorField } from '@/utils/error';
import { routes } from '@/utils/routes';
import { WorkflowStepDefinition } from '@calm/evexia-web-sdk';
import { useStartWorkflow } from '@rhg/hooks/use-start-workflow';
import { logger } from '@rhg/libs/logger';
import { useMutation } from '@rhg/libs/react-query';
import { FtueStoredStepName } from '@rhg/utils/workflows';
import { useRouter } from 'next/router';
import { getCachedVideoId, storeCurrentStep } from '../../utils';

export interface StartFtueWorkflowParams {
    workflowDefnId: string;
    currentStep?: string;
    userSub?: string;
    onStepUpdate: (step: WorkflowStepDefinition | undefined) => void;
    skipCachedVideoStepForward: (
        stepDefnId: string,
        workflowDefnId: string
    ) => Promise<void>;
}

// Mutation hook for starting FTUE workflow
export function useStartFtueWorkflow() {
    const { startWorkflowAsync, isPending } = useStartWorkflow();
    const { replace } = useRouter();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (params: StartFtueWorkflowParams) => {
            const {
                workflowDefnId,
                currentStep,
                userSub,
                onStepUpdate,
                skipCachedVideoStepForward,
            } = params;

            if (currentStep || isPending) {
                return null;
            }

            try {
                const response = await startWorkflowAsync({
                    workflowDefnId,
                });
                const stepObject = response.data?.stepDefn;
                const newStep = stepObject?.defnId;
                const hasCachedVideo = newStep
                    ? !!(await getCachedVideoId(newStep, userSub))
                    : false;

                if (newStep && hasCachedVideo) {
                    await skipCachedVideoStepForward(newStep, workflowDefnId);
                } else {
                    onStepUpdate(stepObject);
                    if (newStep) storeCurrentStep(FtueStoredStepName, newStep);
                }

                return response;
            } catch (error: unknown) {
                logger.error(
                    'Error starting workflow:',
                    {
                        workflowDefnId,
                        currentStep,
                    },
                    error
                );
                if (
                    hasErrorField(error, 'message') &&
                    error.message === 'data_not_authorized'
                )
                    await replace(routes.expired);
                throw error;
            }
        },
    });

    return {
        startFtueWorkflow: mutate,
        startFtueWorkflowAsync: mutateAsync,
        ...otherProps,
    };
}
